import {ArrowUpRightIcon} from '@primer/octicons-react'
import {announce} from '@github-ui/aria-live'
import {useEffect, useRef} from 'react'
import {MiniMergeStatusItem} from './MiniMergeStatusItem'
import {MiniMergeStatusBoundary} from './MiniMergeStatusBoundary'
import {useGoToMergeBox} from '../../../mergebox/use-go-to-mergebox'
import {useMergeabilityData} from '../use-mergeability-data'
import {MultiColorProgressBar} from './MultiColorProgressBar'
import {projectReviewsProgress} from '../reviews-progress-projection'
import type {ReviewsProgressProjection} from '../reviews-progress-projection'
import type {MiniStatusAction} from './MiniMergeStatusBoundary'
import {isForYouRefreshEnabled} from '../../shared/for-you-feature-flags'
import {LegacyApprovalsMiniStatus} from './LegacyApprovalsMiniStatus'

export function ApprovalsMiniStatus() {
  return isForYouRefreshEnabled() ? <RefreshedApprovalsMiniStatus /> : <LegacyApprovalsMiniStatus />
}

function RefreshedApprovalsMiniStatus() {
  const data = useMergeabilityData()
  const goToMergeBox = useGoToMergeBox()
  const projection = projectReviewsProgress(data?.reviewerSection)
  return <ApprovalsMiniStatusView action={goToMergeBox} projection={projection} />
}

export function ApprovalsMiniStatusView({
  action,
  projection,
}: {
  action?: MiniStatusAction
  projection: ReviewsProgressProjection
}) {
  const isRefreshEnabled = isForYouRefreshEnabled()
  const previousUnavailableRef = useRef(projection.isUnavailable)

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- The interactive row remounts its progress subtree when approval data becomes unavailable.
    if (isRefreshEnabled && previousUnavailableRef.current !== projection.isUnavailable) {
      announce(
        projection.isUnavailable
          ? 'No approvals required'
          : [projection.progressLabel, projection.blockingSummary, projection.progressValueText]
              .filter(Boolean)
              .join('. '),
      )
    }
    previousUnavailableRef.current = projection.isUnavailable
  }, [
    isRefreshEnabled,
    projection.blockingSummary,
    projection.isUnavailable,
    projection.progressLabel,
    projection.progressValueText,
  ])

  const interactive = !projection.isUnavailable && action !== undefined
  return (
    <MiniMergeStatusItem
      aria-label={interactive ? 'View approvals' : 'Approvals'}
      // TODO: Update the panel to open the approvals section in the merge status panel. The current open panel options
      // don't support expanding sections within the merge status panel
      onClick={interactive ? event => action(event.currentTarget) : undefined}
    >
      <MiniMergeStatusItem.LabelRow
        icon={interactive ? ArrowUpRightIcon : undefined}
        summary={projection.isUnavailable ? undefined : projection.summary}
      >
        <MiniMergeStatusItem.Label
          icon={projection.icon}
          isUnavailable={projection.isUnavailable}
          status={projection.status}
          successful={projection.successful}
          total={projection.total}
        >
          Approvals
        </MiniMergeStatusItem.Label>
      </MiniMergeStatusItem.LabelRow>
      <MultiColorProgressBar
        aria-label={projection.progressLabel}
        aria-valuetext={projection.progressValueText}
        announceAvailabilityChanges={false}
        blockingSummary={projection.blockingSummary}
        max={projection.total}
        miniStatusSpacing
        semantics="breakdown"
        state={
          projection.isUnavailable
            ? {kind: 'unavailable', label: 'No approvals required'}
            : {kind: 'available', segments: projection.segments}
        }
        value={projection.successful}
      />
    </MiniMergeStatusItem>
  )
}

export function ApprovalsMiniStatusBoundary({
  action,
  children,
}: {
  action?: MiniStatusAction
  children: React.ReactNode
}) {
  const goToMergeBox = useGoToMergeBox()

  return (
    <MiniMergeStatusBoundary
      action={action ?? goToMergeBox}
      actionLabel="View approvals"
      boundaryName="ApprovalsMiniStatusBoundary"
      text="Approvals"
    >
      {children}
    </MiniMergeStatusBoundary>
  )
}
